import React, {
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import throttle from 'lodash/throttle';
import { IconButton } from 'components/Buttons';
import * as Styled from './HorizontalScroll.styles';

export enum ArrowDirection {
  RIGHT = 'right',
  LEFT = 'left',
}

type Props = {
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  showArrows?: boolean;
  isDraggable?: boolean;
  wrapperClassName?: string;
  arrowScrollingPercent?: number;
  parentRef?: MutableRefObject<any>;
  withMobilePadding?: boolean;
};

const HorizontalScroll: React.FC<PropsWithChildren<Props>> = ({
  onMouseUp,
  onMouseDown,
  showArrows = true,
  isDraggable = true,
  children,
  arrowScrollingPercent = 1,
  wrapperClassName,
  withMobilePadding,
  parentRef,
}) => {
  const [arrows, setArrows] = React.useState<Array<boolean>>([false, false]);
  const ref = useRef<HTMLDivElement | null>(null);
  const arrowsRef = useRef<Array<boolean>>([false, false]);
  let pos = { top: 0, left: 0, x: 0, y: 0 };

  const mouseDownHandler = function (e: MouseEvent) {
    if (ref?.current == null) return;
    pos = {
      left: ref.current?.scrollLeft,
      top: ref.current?.scrollTop,
      x: e.clientX,
      y: e.clientY,
    };
    ref.current.style.cursor = 'grabbing';
    ref.current.style.userSelect = 'none';

    ref.current?.addEventListener('mousemove', mouseMoveHandler);
    ref.current?.addEventListener('mouseup', mouseUpHandler);
    onMouseDown?.();
  };

  const mouseUpHandler = function () {
    if (ref?.current == null) return;
    ref.current.removeEventListener('mousemove', mouseMoveHandler);
    ref.current.removeEventListener('mouseup', mouseUpHandler);
    ref.current.style.cursor = 'pointer';
    ref.current.style.removeProperty('user-select');
    onMouseUp?.();
  };

  const mouseMoveHandler = function (e: MouseEvent) {
    if (ref?.current == null) return;
    const dx = e.clientX - pos.x;
    ref.current.scrollLeft = pos.left - dx;
  };

  useEffect(() => {
    if (ref?.current == null) return;
    setArrows(() => {
      return [
        ref.current?.scrollLeft !== 0,

        // @ts-ignore
        ref.current?.scrollWidth > ref.current?.offsetWidth,
      ];
    });

    if (isDraggable)
      ref.current.addEventListener('mousedown', mouseDownHandler);

    return () => cleanComponent();
  }, [children]);

  React.useImperativeHandle(parentRef, () => ({
    resetScroll() {
      ref.current?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    },
  }));

  const cleanComponent = () => {
    ref.current?.removeEventListener('mousedown', mouseMoveHandler);
    ref.current?.removeEventListener('scroll', mouseUpHandler);
  };

  const onNextClick = () => {
    ref.current?.scrollTo({
      left:
        ref.current?.scrollLeft +
        ref.current?.offsetWidth * arrowScrollingPercent,
      behavior: 'smooth',
    });
  };

  const onPrevClick = () => {
    ref.current?.scrollTo({
      left:
        ref.current?.scrollLeft -
        ref.current?.offsetWidth * arrowScrollingPercent,
      behavior: 'smooth',
    });
  };

  const handleConfig = (start: boolean, end: boolean) => {
    arrowsRef.current = [start, end];
    setArrows([start, end]);
  };

  const throttleGetArrows = throttle(() => {
    if (ref?.current == null) return;
    // To prevent issue, when difference is less then 2px, and arrow is still shown, but scroll not working
    const isNotEnd =
      ref.current?.scrollWidth -
        (ref.current?.offsetWidth + ref.current?.scrollLeft) >
      2;
    const isNotStart = ref.current?.scrollLeft !== 0;
    if (
      arrowsRef.current[0] === isNotStart &&
      arrowsRef.current[1] === isNotEnd
    )
      return;
    handleConfig(isNotStart, isNotEnd);
  }, 100);

  return (
    <Box width="100%" position="relative" className={wrapperClassName}>
      <Arrow
        direction={ArrowDirection.LEFT}
        onClick={onPrevClick}
        show={showArrows && arrows[0]}
      />
      <Styled.Wrapper
        ref={ref}
        onScroll={throttleGetArrows}
        withMobilePadding={withMobilePadding}
      >
        {children}
      </Styled.Wrapper>
      <Arrow
        direction={ArrowDirection.RIGHT}
        onClick={onNextClick}
        show={showArrows && arrows[1]}
      />
    </Box>
  );
};

type ArrowProps = {
  direction: ArrowDirection;
  onClick: () => void;
  show: boolean;
};

const Arrow: React.FC<ArrowProps> = React.memo(
  ({ direction, onClick, show }) => {
    return (
      <Styled.Arrow direction={direction} show={show}>
        {direction === ArrowDirection.RIGHT && (
          <Styled.Shadow direction={direction} />
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            backgroundColor: 'white',
          }}
        >
          <IconButton
            onClick={onClick}
            size="small"
            color="secondary"
            iconName={
              ArrowDirection.RIGHT === direction ? 'arrow-right' : 'arrow-left'
            }
          />
        </Box>
        {direction === ArrowDirection.LEFT && (
          <Styled.Shadow direction={direction} />
        )}
      </Styled.Arrow>
    );
  },
);

Arrow.displayName = 'Arrow';

export default HorizontalScroll;
